<template>
  <div class="relative">
    <!-- Mock Website Background -->
    <div class="w-full h-[500px] bg-dark-secondary rounded-lg overflow-hidden">
      <!-- Mock Header -->
      <div class="h-16 bg-dark border-b border-gray-700 flex items-center px-6">
        <div class="w-32 h-4 bg-gray-700 rounded"></div>
        <div class="ml-auto flex items-center space-x-6">
          <div class="w-16 h-4 bg-gray-700 rounded"></div>
          <div class="w-16 h-4 bg-gray-700 rounded"></div>
          <div class="w-16 h-4 bg-gray-700 rounded"></div>
        </div>
      </div>

      <!-- Mock Content -->
      <div class="p-6 grid grid-cols-4 gap-4">
        <div v-for="i in 8" :key="i" class="aspect-square bg-dark rounded-lg"></div>
      </div>
    </div>

    <!-- Search Widget Preview -->
    <div
      class="absolute inset-0 bg-black/50"
      :class="{
        'p-4': device === 'mobile',
        'p-8': device === 'tablet',
        'p-12': device === 'desktop'
      }"
    >
      <!-- Widget Container -->
      <div
        class="absolute left-0 right-0 bottom-4 mx-auto w-fit max-w-[90%] transition-all duration-200 hover:scale-[1.03] focus-within:hover:scale-100 group"
        :style="{
          fontFamily: customization.appearance.thread.font || 'sans-serif'
        }"
      >
        <!-- Form -->
        <div
          class="relative flex items-center rounded-full backdrop-blur-2xl shadow-sm"
          :style="{
            backgroundColor: customization.appearance.input.background,
            borderColor: customization.appearance.input.highlight,
            borderWidth: '1px'
          }"
        >
          <!-- Search Icon -->
          <div class="absolute left-3 flex items-center pointer-events-none">
            <svg
              class="w-4 h-4"
              :style="{ color: customization.appearance.input.placeholder.color }"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <!-- Input -->
          <input
            type="text"
            :placeholder="placeholder"
            class="w-[300px] h-[2.3em] pl-10 pr-12 py-2 bg-transparent outline-none"
            :style="{
              color: customization.appearance.input.color,
              fontSize: customization.appearance.input.size
            }"
            @focus="showSuggestions = true; clearTypewriter()"
            @blur="showSuggestions = false; typeWriter(customization.promptSuggestions[currentSuggestionIndex].value)"
          >

          <!-- Submit Button -->
          <button
            class="absolute right-2 w-8 h-8 flex items-center justify-center rounded-full transition-transform hover:scale-110"
            :style="{
              backgroundColor: customization.appearance.cta.background,
              color: '#ffffff'
            }"
          >
            <svg
              class="w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 12h14m-7-7l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        <!-- Prompt Suggestions -->
        <div
          class="absolute bottom-full mb-2 left-0 right-0 border rounded-t-2xl overflow-hidden transition-all duration-300 transform origin-bottom"
          :class="[
            showSuggestions ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
          ]"
          :style="{
            backgroundColor: customization.appearance.input.background,
            borderColor: customization.appearance.input.highlight,
            clipPath: showSuggestions ? 'inset(0 0 0 0)' : 'inset(100% 0 0 0)',
            paddingBottom: '1.5em'
          }"
          @mouseenter="clearTypewriter"
          @mouseleave="() => !showSuggestions && typeWriter(customization.promptSuggestions[currentSuggestionIndex].value)"
        >
          <div
            v-for="(suggestion, index) in customization.promptSuggestions"
            :key="index"
            class="px-4 py-3 cursor-pointer transition-colors text-sm"
            :style="{
              color: customization.appearance.input.placeholder.color,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              }
            }"
          >
            {{ suggestion.value }}
          </div>
        </div>
            </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

interface Props {
  customization: {
    appearance: {
      thread: {
        color: string
        background: string
        font: string
        linkColor: string
      }
      input: {
        color: string
        background: string
        size: string
        highlight: string
        placeholder: {
          text: string
          color: string
        }
      }
      cta: {
        background: string
      }
    }
    promptSuggestions: Array<{ value: string }>
    defaultFont: string
  }
  device: 'desktop' | 'tablet' | 'mobile'
}

const props = defineProps<Props>()

// Typewriter effect
const baseText = "Ask anything..."
const currentSuggestionIndex = ref(0)
const placeholder = ref(baseText)
const showSuggestions = ref(false)
let typewriterTimeout: number | undefined

const typeWriter = (text: string, isTyping = true, i = 0) => {
  if (isTyping) {
    if (i < text.length) {
      placeholder.value = baseText + text.substring(0, i + 1)
      typewriterTimeout = window.setTimeout(() => typeWriter(text, true, i + 1), 50)
    } else {
      typewriterTimeout = window.setTimeout(() => typeWriter(text, false, text.length), 1000)
    }
  } else {
    if (i > 0) {
      placeholder.value = baseText + text.substring(0, i - 1)
      typewriterTimeout = window.setTimeout(() => typeWriter(text, false, i - 1), 50)
    } else {
      currentSuggestionIndex.value = (currentSuggestionIndex.value + 1) % props.customization.promptSuggestions.length
      typewriterTimeout = window.setTimeout(
        () => typeWriter(props.customization.promptSuggestions[currentSuggestionIndex.value].value, true, 0),
        500
      )
    }
  }
}

const clearTypewriter = () => {
  if (typewriterTimeout) {
    window.clearTimeout(typewriterTimeout)
  }
  placeholder.value = baseText
}

onMounted(() => {
  typeWriter(props.customization.promptSuggestions[currentSuggestionIndex.value].value)
})

onUnmounted(() => {
  if (typewriterTimeout) {
    window.clearTimeout(typewriterTimeout)
  }
})
</script> 